import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import type { Product, ProductVariant } from '../../types';
import { ProductVariantSelects } from './ProductVariantSelects';

export type PromotionType = '1+1' | '2+1' | '3+1' | '4+1' | 'free_samples' | 'none';

export const PROMOTION_OPTIONS: { value: PromotionType; label: string }[] = [
  { value: 'none', label: 'No Promo' },
  { value: '1+1', label: '1+1 Free' },
  { value: '2+1', label: '2+1 Free' },
  { value: '3+1', label: '3+1 Free' },
  { value: '4+1', label: '4+1 Free' },
  { value: 'free_samples', label: 'Free Samples' },
];

export interface OrderLineDraft {
  key: string;
  id?: string;
  productId: string;
  variantId: string;
  quantity: string;
  promotionType: PromotionType;
  priceOverride: string;
  notes: string;
  picked?: boolean;
}

interface EditOrderLineFieldsProps {
  products: Product[];
  line: OrderLineDraft;
  controlClass: string;
  onChange: (next: OrderLineDraft) => void;
}

export const EditOrderLineFields: React.FC<EditOrderLineFieldsProps> = ({
  products,
  line,
  controlClass,
  onChange,
}) => {
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  useEffect(() => {
    if (!line.productId) {
      setVariants([]);
      return;
    }
    let active = true;
    api
      .get(`/products/${line.productId}/variants`)
      .then((res) => {
        if (!active) return;
        const list = res.data as ProductVariant[];
        setVariants(list);
        if (list.length === 1 && !line.variantId) {
          onChange({ ...line, variantId: list[0].id });
        }
      })
      .catch(() => toast.error('Failed to load product variants'));
    return () => {
      active = false;
    };
  }, [line.productId]);

  return (
    <div className="space-y-2">
      <ProductVariantSelects
        products={products}
        variants={variants}
        productId={line.productId}
        variantId={line.variantId}
        onProductChange={(productId) =>
          onChange({ ...line, productId, variantId: '' })
        }
        onVariantChange={(variantId) => onChange({ ...line, variantId })}
        formatVariantLabel={(v) =>
          `${v.gebinde_name} (${v.units_per_case}× ${v.unit_volume || ''})`
        }
        controlClass={controlClass}
      />
      <label className="block text-sm font-medium text-gray-700">
        Quantity
        <input
          type="number"
          min={1}
          inputMode="numeric"
          value={line.quantity}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange({ ...line, quantity: e.target.value })}
          className={`${controlClass} mt-1`}
        />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Promotion
        <select
          value={line.promotionType}
          onChange={(e) =>
            onChange({ ...line, promotionType: e.target.value as PromotionType })
          }
          className={`${controlClass} mt-1`}
        >
          {PROMOTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Price override (EUR/case)
        <input
          type="number"
          min={0}
          step="0.01"
          value={line.priceOverride}
          onChange={(e) => onChange({ ...line, priceOverride: e.target.value })}
          className={`${controlClass} mt-1`}
        />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Notes
        <input
          value={line.notes}
          onChange={(e) => onChange({ ...line, notes: e.target.value })}
          className={`${controlClass} mt-1`}
        />
      </label>
    </div>
  );
};
